import { useState, useEffect, lazy, Suspense } from "react";
import Header from "./components/layout/Header";
import MobileCTABar from "./components/layout/MobileCTABar";
import HomePage from "./pages/HomePage";
import { initAnalytics, trackEvent } from "./utils/analytics";
import {
  LEGACY_REDIRECTS,
  pathToPage,
  titleForPath,
} from "./lib/site";

const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const PackagesPage = lazy(() => import("./pages/PackagesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AdminAds = lazy(() => import("./components/AdminAds"));

export default function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    initAnalytics();
  }, []);

  // Client-side redirects for retired marketing URLs
  useEffect(() => {
    const target = LEGACY_REDIRECTS[currentPath];
    if (target) {
      window.history.replaceState({}, "", target);
      setCurrentPath(target);
    }
  }, [currentPath]);

  useEffect(() => {
    const pageTitle = titleForPath(currentPath);
    document.title = pageTitle;
    trackEvent("page_view", {
      page_path: currentPath,
      page_title: pageTitle,
    });
  }, [currentPath]);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener("popstate", handleLocationChange);

    const interval = setInterval(() => {
      if (window.location.pathname !== currentPath) {
        setCurrentPath(window.location.pathname);
      }
    }, 500);

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
      clearInterval(interval);
    };
  }, [currentPath]);

  const navigateTo = (path: string) => {
    window.history.pushState({}, "", path);
    setCurrentPath(path);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentPage = pathToPage(currentPath);
  const isAdmin = currentPath === "/admin-ads";

  const renderView = () => {
    if (isAdmin) {
      return <AdminAds onNavigateHome={() => navigateTo("/")} />;
    }

    switch (currentPath) {
      case "/services":
        return <ServicesPage navigate={navigateTo} />;
      case "/packages":
        return <PackagesPage navigate={navigateTo} />;
      case "/contact":
        return <ContactPage navigate={navigateTo} />;
      case "/":
      default:
        return <HomePage navigate={navigateTo} />;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground antialiased">
      {!isAdmin && (
        <Header
          currentPage={currentPage}
          navigate={navigateTo}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
      )}

      <main className={isAdmin ? undefined : "pb-16 md:pb-0"}>
        <Suspense fallback={null}>{renderView()}</Suspense>
      </main>

      {!isAdmin && <MobileCTABar navigate={navigateTo} />}
    </div>
  );
}
